import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { AuthService } from "../../../auth/services/auth.service";
import {
    PricingAmountLabelParts,
    PricingBillingPeriod,
    PricingPlan,
    PricingService,
} from "../../../shared/services/pricing.service";

interface ProductScreenshot {
    /** Stable picsum seed — replace with real product captures later. */
    seed: string;
    label: string;
    alt: string;
}

interface FeatureGroup extends ProductScreenshot {
    icon: string;
    /** Pastille type pricing-plan__badge (ex. « Dès l'essai »). */
    tier: string;
    title: string;
    lede: string;
    points: string[];
    featured?: boolean;
}

interface Step extends ProductScreenshot {
    title: string;
    text: string;
}

interface PainPoint {
    index: string;
    title: string;
    text: string;
}

interface SolutionCapability {
    index: string;
    tier: string;
    title: string;
    detail: string;
}

interface Testimonial {
    quote: string;
    name: string;
    role: string;
}

/** Precomputed when `pricingPlans` or `billingPeriod` changes — avoids template method calls each CD cycle. */
interface PricingPlanRow {
    plan: PricingPlan;
    priceParts: PricingAmountLabelParts | null;
    plainLabel: string;
    hasYearlySavingsSubtitle: boolean;
}

@Component({
    selector: "app-home",
    templateUrl: "./home-one.component.html",
    styleUrls: ["./home-one.component.scss"],
})
export class HomeOneComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("pageRoot") pageRoot?: ElementRef<HTMLElement>;

    /** Hero entrance choreography (first paint). */
    pageEntered = false;

    private revealObserver?: IntersectionObserver;

    pricingPlans: PricingPlan[] = [];
    /** Lignes d’affichage tarifs (dérivées de `pricingPlans` + `billingPeriod`). */
    pricingRows: PricingPlanRow[] = [];
    /** Au moins un plan a un mensuel de référence : afficher le toggle mensuel / annuel. */
    showBillingPeriodToggle = false;
    pricingLoading = true;
    /** Fréquence choisie pour les cartes et pour `/checkout?billing=…`. */
    billingPeriod: PricingBillingPeriod = "yearly";

    constructor(
        private readonly router: Router,
        private readonly auth: AuthService,
        private readonly pricing: PricingService,
    ) {}

    ngOnInit(): void {
        requestAnimationFrame(() => {
            this.pageEntered = true;
        });

        this.pricing
            .getPlans()
            .pipe(finalize(() => (this.pricingLoading = false)))
            .subscribe((plans) => {
                this.pricingPlans = plans;
                this.syncPricingDisplay();
                requestAnimationFrame(() => this.observeReveals());
            });
    }

    ngAfterViewInit(): void {
        if (typeof IntersectionObserver === "undefined") {
            this.markAllRevealsVisible();
            return;
        }

        this.revealObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }
                    entry.target.classList.add("is-visible");
                    this.revealObserver?.unobserve(entry.target);
                }
            },
            { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
        );

        this.observeReveals();
    }

    ngOnDestroy(): void {
        this.revealObserver?.disconnect();
    }

    private observeReveals(): void {
        const root = this.pageRoot?.nativeElement;
        if (!root || !this.revealObserver) {
            return;
        }

        root.querySelectorAll<HTMLElement>("[data-ray-reveal]:not(.is-visible)").forEach((el) => {
            this.revealObserver!.observe(el);
        });
    }

    private markAllRevealsVisible(): void {
        const root = this.pageRoot?.nativeElement;
        if (!root) {
            return;
        }
        root.querySelectorAll<HTMLElement>("[data-ray-reveal]").forEach((el) => {
            el.classList.add("is-visible");
        });
    }

    setBillingPeriod(period: PricingBillingPeriod): void {
        this.billingPeriod = period;
        this.syncPricingDisplay();
    }

    private syncPricingDisplay(): void {
        this.showBillingPeriodToggle = this.pricingPlans.some(
            (p) => this.pricing.referenceMonthlyMga(p) != null,
        );
        this.pricingRows = this.pricingPlans.map((plan) => {
            const priceParts = this.pricing.amountLabelPartsForPlan(plan, this.billingPeriod);
            return {
                plan,
                priceParts,
                plainLabel: this.pricing.amountLabelForPlan(plan, this.billingPeriod),
                hasYearlySavingsSubtitle: !!priceParts?.yearlySubtitle,
            };
        });
    }

    /**
     * Free trial or paid plan: guest → signup/login then checkout; logged-in → checkout.
     */
    startCheckout(planId: string): void {
        const returnUrl = this.auth.buildCheckoutUrl(planId, this.billingPeriod);
        if (this.auth.isLoggedIn) {
            void this.router.navigateByUrl(returnUrl);
            return;
        }
        void this.router.navigate(["/auth/signup"], { queryParams: { returnUrl } });
    }

    pains: PainPoint[] = [
        {
            index: "01",
            title: "Ventes mal suivies",
            text: "Commandes et relances mélangées aux conversations, sans historique fiable.",
        },
        {
            index: "02",
            title: "Factures et paiements flous",
            text: "Difficile de savoir ce qui est facturé, encaissé ou encore dû quand l'urgence arrive.",
        },
        {
            index: "03",
            title: "Stocks et achats tardifs",
            text: "Sans alertes ni variantes, les ruptures et les commandes fournisseurs arrivent trop tard.",
        },
        {
            index: "04",
            title: "Équipe sans cadre",
            text: "À plusieurs, chacun ne doit pas tout voir : rôles et permissions adaptés à la tâche.",
        },
    ];

    readonly heroScreenshot: ProductScreenshot = {
        seed: "rayalina-dashboard-hero",
        label: "Tableau de bord",
        alt: "Aperçu du tableau de bord Rayalina avec ventes et encaissements",
    };

    readonly solutionScreenshots: ProductScreenshot[] = [
        {
            seed: "rayalina-workflow",
            label: "Vue d'ensemble",
            alt: "Aperçu de l'interface Rayalina reliant commandes, factures et paiements",
        },
        {
            seed: "rayalina-orders-board",
            label: "Commandes & factures",
            alt: "Aperçu des commandes et factures avec suivi des paiements",
        },
    ];

    solutionCapabilities: SolutionCapability[] = [
        {
            index: "01",
            tier: "Dès l'essai",
            title: "Ventes, factures, encaissements",
            detail: "Commandes et factures, avec suivi des paiements et fiches clients.",
        },
        {
            index: "02",
            tier: "Au quotidien",
            title: "Pilotage et exports",
            detail: "Tableau de bord quotidien, historique des ventes, exports PDF ou Excel.",
        },
        {
            index: "03",
            tier: "Quand le volume monte",
            title: "Achats, stocks, variantes",
            detail: "Achats, fournisseurs, stocks, articles, variantes, alertes de rupture.",
        },
        {
            index: "04",
            tier: "Structure mature",
            title: "Équipe et entrepôts",
            detail: "Rôles, multi-entrepôts, rapports et support prioritaire.",
        },
    ];

    productShotSrc(seed: string, width = 1120, height = 700): string {
        return `https://picsum.photos/seed/${seed}/${width}/${height}`;
    }

    /** Bloc principal avec capture ; le reste est regroupé sans visuel répété. */
    featureLead: FeatureGroup = {
        icon: "bx-layer",
        seed: "rayalina-sales-cycle",
        label: "Cycle vente",
        tier: "Dès l'essai",
        featured: true,
        alt: "Aperçu Rayalina : commandes, factures, paiements et fiches clients",
        title: "Ventes, factures et encaissements",
        lede: "Dès l'essai gratuit, tout le cycle client tient au même endroit : vous enregistrez les commandes, émettez des factures claires, voyez qui a payé ou ce qui reste dû en MGA, et gardez une fiche par client avec son historique — sans repasser par Messenger ni des fichiers éparpillés.",
        points: [
            "Commandes et relances structurées, hors des fils de discussion",
            "Factures traçables pour chaque client",
            "Paiements et soldes visibles au quotidien",
            "Fiches clients avec le contexte immédiat de la relation",
        ],
    };

    featureMore: FeatureGroup[] = [
        {
            icon: "bx-line-chart",
            seed: "rayalina-dashboard",
            label: "Pilotage",
            tier: "Formules actives",
            alt: "Capture d'écran du tableau de bord et des indicateurs quotidiens",
            title: "Tableau de bord et exports",
            lede: "Une vue simple ou plus détaillée selon votre formule : indicateurs pour décider vite, historique des ventes, et exports PDF ou Excel pour votre comptable, vos associés ou vos archives.",
            points: [
                "Indicateurs quotidiens sans tableur annexe",
                "Historique des ventes consultable en quelques clics",
                "Exports PDF ou Excel quand il faut partager ou archiver",
            ],
        },
        {
            icon: "bx-buildings",
            seed: "rayalina-operations",
            label: "Opérations",
            tier: "Volume croissant",
            alt: "Aperçu des modules stocks, fournisseurs, achats et rôles équipe",
            title: "Stocks, achats et équipe",
            lede: "Quand le volume augmente, vous activez achats et fournisseurs, articles et variantes, alertes de rupture, puis rôles, multi-entrepôts et rapports — toujours dans le même outil, sans reperdre commandes ni historique client.",
            points: [
                "Articles, variantes, stocks et alertes de rupture",
                "Achats et fournisseurs liés aux ventes",
                "Utilisateurs, permissions et multi-entrepôts sur les formules supérieures",
            ],
        },
    ];

    steps: Step[] = [
        {
            seed: "rayalina-scale",
            label: "Essai",
            alt: "Aperçu de la création d'espace et du premier tableau de bord",
            title: "Créez votre espace et testez",
            text: "Profitez de l'essai gratuit pour enregistrer commandes, factures, paiements et clients dans un tableau de bord simple.",
        },
        {
            seed: "rayalina-onboard",
            label: "Quotidien",
            alt: "Aperçu de l'historique des ventes et des exports",
            title: "Organisez vos ventes au quotidien",
            text: "Historique des ventes, exports PDF ou Excel : tout ce qu'il faut pour avancer sereinement.",
        },
        {
            seed: "rayalina-scale",
            label: "Croissance",
            alt: "Aperçu des modules stocks, fournisseurs et équipe",
            title: "Montez en gamme quand votre activité grandit",
            text: "Passez aux formules avec achats, stocks et fournisseurs, puis rôles, multi-entrepôts et rapports avancés lorsque votre structure le demande.",
        },
    ];

    testimonials: Testimonial[] = [
        {
            quote: "Les factures et le suivi des paiements m'ont enlevé une grosse charge mentale. Je vois enfin ce qui est réglé ou non.",
            name: "Lylia R.",
            role: "Vendeuse mode, Antananarivo",
        },
        {
            quote: "Le stock et les fournisseurs au même endroit que les ventes, ça nous a fait gagner un temps fou sur les commandes récurrentes.",
            name: "Franco Market",
            role: "Commerce de détail, Réseaux sociaux",
        },
        {
            quote: "On a ajouté deux collègues avec des rôles différents sans que tout se mélange. L'outil reste simple pour celui qui ne fait que les encaissements.",
            name: "Carmilla Beauty",
            role: "Boutique locale, Fianarantsoa",
        },
    ];
}
